import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AFPTable,
  EmptyState,
  AFPTableRowAction,
  Pagination,
  StatusBadge,
} from '@gsa/afp-component-library';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import { ModelMakeStatuses, MAKE_MODEL_ENTRY_SOURCE } from '../constants';
import {
  OPERATIONS,
  SUBJECTS,
  emDashUnicode,
} from '../../../utilities/constants';
import ModelApproveRejectModal from './model-approve-reject-modal';
import EditMakeModelModal from '../edit-make-model/index';

export const MakeModelTable = ({
  tableProps,
  paginationState,
  setPaginationState,
}) => {
  const ability = useAppAbility();
  const canUpdateMakeModel = ability.can(OPERATIONS.Update, SUBJECTS.makeModel);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMakeModel, setSelectedMakeModel] = useState({});
  const [action, setAction] = useState(null);


  const handleSelectedAction = (evt, row) => {
    setSelectedMakeModel(row.original);
    if (evt === 'Edit') {
      setIsEditModalOpen(true);
      return;
    }
    setAction(evt);
    setIsOpen(true);
  };

  const closeModelApprovalModel = () => {
    setIsOpen(false);
  };

  const getMMCombinationFleetStatus = (MakeFleetStatus, ModelFleetStatus) => {
    const combination = `${MakeFleetStatus}-${ModelFleetStatus}`;

    const statusMap = new Map([
      ['Approved-Approved', { badge: 'Ready-Gray', status: 'Approved' }],
      ['Rejected-Approved', { badge: 'Urgent-Gray', status: 'Rejected' }],
      ['Approved-Rejected', { badge: 'Urgent-Gray', status: 'Rejected' }],
      ['Rejected-PendingReview', { badge: 'Urgent-Gray', status: 'Rejected' }],
      ['PendingReview-Rejected', { badge: 'Urgent-Gray', status: 'Rejected' }],
      ['Rejected-Rejected', { badge: 'Urgent-Gray', status: 'Rejected' }],
      [
        'Approved-PendingReview',
        { badge: 'Warning-Gray', status: 'Pending review' },
      ],
      [
        'PendingReview-Approved',
        { badge: 'Warning-Gray', status: 'Pending review' },
      ],
      [
        'PendingReview-PendingReview',
        { badge: 'Warning-Gray', status: 'Pending review' },
      ],
      ['Approved-Archived', { badge: 'Inactive-Gray', status: 'Archived' }],
      ['Archived-Approved', { badge: 'Inactive-Gray', status: 'Archived' }],
      ['Archived-Archived', { badge: 'Inactive-Gray', status: 'Archived' }],
    ]);

    return (
      statusMap.get(combination) || {
        badge: 'Warning-Gray',
        status: 'Pending review',
      }
    );
  };

  // entrySource may come back as a numeric code (e.g. 0) or its string enum
  // name (e.g. 'NHTSA'), so match against both representations.
  const isEntrySource = (entrySource, key) =>
    entrySource === MAKE_MODEL_ENTRY_SOURCE[key] || entrySource === key;

  // Make/model codes can be UIDs; when so, show only the first 8 characters.
  const formatCode = (code) =>
    code && code.length > 10 ? code.substring(0, 8) : code;

  // Maps the model's entrySource to its display label in the Source column.
  const getSourceLabel = (entrySource) => {
    if (isEntrySource(entrySource, 'NHTSA')) return 'NHTSA';
    if (isEntrySource(entrySource, 'VMS')) return 'VMS';
    if (isEntrySource(entrySource, 'LEGACY')) return 'Legacy';
    return 'GSA Engineer';
  };

  const getActions = (row) => {
    const { fleetStatus, entrySource, fleetApprovedByUser } = row.original;

    const isNhtsa = isEntrySource(entrySource, 'NHTSA');
    const isVmsOrLegacy =
      isEntrySource(entrySource, 'VMS') ||
      isEntrySource(entrySource, 'LEGACY');

    // VMS & Legacy records without a fleet approver have no available actions,
    // which hides the ellipsis in the Actions column.
    if (isVmsOrLegacy && !fleetApprovedByUser) {
      return [];
    }

    const actions = [];

    // Approve/Reject toggle based on the current fleet status.
    if (fleetStatus === ModelMakeStatuses.Approved.value) {
      actions.push({
        icon: 'close',
        label: 'Reject',
      });
    } else if (fleetStatus === ModelMakeStatuses.Rejected.value) {
      actions.push({
        icon: 'check',
        label: 'Approve',
      });
    }

    // Edit is available for every source except NHTSA.
    if (!isNhtsa) {
      actions.push({
        icon: 'edit',
        label: 'Edit',
      });
    }

    return actions;
  };

  const useColumns = () => {
    const columns = useMemo(
      () => [
        {
          Header: 'Make',
          accessor: 'make.makeName',
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => {
            // eslint-disable-next-line react/prop-types
            const { make } = row.original;
            const code = formatCode(make?.nhtsaMakeCode || make?.makeCode);
            return (
              <div>
                <div>{make?.makeName}</div>
                <div className="text-base">{code}</div>
              </div>
            );
          },
        },
        {
          Header: 'Model',
          accessor: 'modelName',
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => {
            const {
              // eslint-disable-next-line react/prop-types
              modelName,
              // eslint-disable-next-line react/prop-types
              nhtsaModelCode,
              // eslint-disable-next-line react/prop-types
              modelCode,
            } = row.original;
            const code = formatCode(nhtsaModelCode || modelCode);
            return (
              <div>
                <div>{modelName}</div>
                <div className="text-base">{code}</div>
              </div>
            );
          },
        },
        {
          Header: 'Fleet status',
          accessor: 'fleetStatus',
          sortable: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => {
            const {
              make: { fleetStatus: makeFleetStatus },
              fleetStatus: modelFleetStatus,
            } = row.original;
            const variant = getMMCombinationFleetStatus(
              makeFleetStatus,
              modelFleetStatus,
            ).badge;
            const value = getMMCombinationFleetStatus(
              makeFleetStatus,
              modelFleetStatus,
            ).status;

            return (
              variant && <StatusBadge variant={variant}>{value}</StatusBadge>
            );
          },
        },
        {
          Header: 'NHTSA status',
          sortable: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => {
            const {
              // eslint-disable-next-line react/prop-types
              make: { nhtsaStatus: makeNhtsaStatus },
              // eslint-disable-next-line react/prop-types
              nhtsaStatus: modelNhtsaStatus,
              // eslint-disable-next-line react/prop-types
              entrySource,
            } = row.original;
            // NHTSA status only applies to NHTSA-sourced records.
            if (!isEntrySource(entrySource, 'NHTSA')) {
              return emDashUnicode;
            }
            // The nhtsaStatus field comes back as a label ('Active'/'Inactive');
            // active only when both the make and the model are NHTSA active.
            const isActive =
              makeNhtsaStatus === 'Active' && modelNhtsaStatus === 'Active';
            return (
              <StatusBadge variant={isActive ? 'Ready-Gray' : 'Inactive-Gray'}>
                {isActive ? 'Active' : 'Inactive'}
              </StatusBadge>
            );
          },
        },
        {
          Header: 'Source',
          accessor: 'entrySource',
          sortable: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => getSourceLabel(row.original.entrySource),
        },
        {
          Header: 'Actions',
          sortable: false,
          minWidth: 30,
          width: 150,
          maxWidth: 400,
          Cell: (props) => {
            const actions = canUpdateMakeModel ? getActions(props.row) : [];
            return actions.length ? (
              <AFPTableRowAction
                {...props}
                actions={actions}
                onSelectAction={(evt) => handleSelectedAction(evt, props.row)}
              />
            ) : null;
          },
        },
      ],
      [canUpdateMakeModel],
    );
    return columns;
  };

  const columns = useColumns();
  const isNoData = !tableProps?.data?.length;

  return (
    <>
      <AFPTable data={tableProps?.data} columns={columns} {...tableProps} />
      {tableProps?.data.length > 0 && (
        <Pagination
          itemsPerPageOptions={[50, 100, 150, 200]}
          variant="advanced"
          onPageChange={(currentPage, itemsPerPage) => {
            const newState = {
              ...paginationState,
              currentPage,
              offset: (currentPage - 1) * itemsPerPage,
              limit: itemsPerPage,
            };
            setPaginationState(newState);
          }}
          itemsPerPage={paginationState.limit}
          currentPage={paginationState.currentPage}
          itemsCount={tableProps.count}
        />
      )}
      {isNoData && (
        <div className="text-center margin-top-neg-2 height-full">
          <EmptyState
            hasBackground
            containerStyles="padding-y-6 height-full"
            topText={<p>No data is available.</p>}
          />
        </div>
      )}
      {/* Modals section */}
      <ModelApproveRejectModal
        isOpen={isOpen}
        data={selectedMakeModel}
        action={action}
        onClose={closeModelApprovalModel}
        refetchData={tableProps?.refetchMakeModel}
      />
      <EditMakeModelModal
        isOpen={isEditModalOpen}
        data={selectedMakeModel}
        onClose={() => setIsEditModalOpen(false)}
        refetchData={tableProps?.refetchMakeModel}
      />
    </>
  );
};

MakeModelTable.propTypes = {
  tableProps: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
  }).isRequired,
  paginationState: PropTypes.shape({
    currentPage: PropTypes.number,
    offset: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  setPaginationState: PropTypes.func.isRequired,
};

